import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import classNames from 'classnames'
import { type CSSProperties, useLayoutEffect, useMemo, useState } from 'react'
import { Spacing } from '@opallabs/gem'
import { Avatar, DEFAULT_SIZE } from '../Avatar'
import { cssVariables, parts, selectors } from './styleHooks'
import './styles.css'
import { type AvatarListProps, type Item } from './types'

export { type AvatarListProps }
export { parts as avatarListParts, root as avatarListRoot } from './styleHooks'

// @note Usually it would be more efficient to create an `Intl.Collator` instance and use it to sort the array, rather
// than having `localeCompare` repeatedly create a new instance for each comparison. Here, however, it's unhelpful
// because the duration is about the same on each `sortItems` call; with `localeCompare` the first run on a given page
// load may be slower than `Intl.Collator`, but all subsequent runs are significantly faster than what `Intl.Collator`
// can offer. (As observed in Chrome 147 and Safari 26.4, using `console.time`: both optimize `localeCompare` to be
// 50-200x faster.) There's presumably some JIT hot path that kicks in.
const sortItems = (items: Item[]): Item[] =>
  items.toSorted((a, b) => a.label.localeCompare(b.label))

// Limits the amount of names the screenreader announces. The amount is arbitrary, trying to give something useful
// without overwhelming the user.
const maxItemsInAccessibleText = 4

const avatarStackCountForWidth = ({
  avatarSize,
  overlapAmount,
  width,
}: Record<'avatarSize' | 'overlapAmount' | 'width', number>) =>
  Math.max(
    0,
    Math.floor((width - overlapAmount) / (avatarSize - overlapAmount))
  )

// `getBoundingClientRect` and `clientWidth` include padding; `ResizeObserver`'s `contentRect` doesn't
const contentWidth = (el: HTMLElement) => {
  const { paddingInlineEnd, paddingInlineStart } = getComputedStyle(el)
  return (
    el.clientWidth -
    (parseFloat(paddingInlineStart) || 0) -
    (parseFloat(paddingInlineEnd) || 0)
  )
}

export const AvatarList: React.FC<AvatarListProps> = ({
  avatarSize = DEFAULT_SIZE,
  className,
  exportparts,
  hideMoreItemsContainer,
  initialsLength,
  items,
  locale,
  maxDisplayedItems,
  overlapAmount = Spacing.XS,
  sortMode = 'sort',
  tooltipped = true,
}) => {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const [measuredWidth, setMeasuredWidth] = useState(0)

  useLayoutEffect(() => {
    const parent = containerEl?.parentElement
    if (!parent) return

    // Only update `measuredWidth` if it will affect the number of rendered avatars
    const cap = maxDisplayedItems ?? Infinity
    const stackCount = (width: number) =>
      Math.min(
        cap,
        avatarStackCountForWidth({ avatarSize, overlapAmount, width })
      )

    setMeasuredWidth(contentWidth(parent))
    const observer = new ResizeObserver(([entry]) => {
      const next = entry.contentRect.width
      requestAnimationFrame(() =>
        setMeasuredWidth((prev) =>
          stackCount(prev) === stackCount(next) ? prev : next
        )
      )
    })
    observer.observe(parent)

    return () => observer.disconnect()
  }, [containerEl, avatarSize, overlapAmount, maxDisplayedItems])

  const orderedItems = useMemo(
    () => (sortMode === 'presorted' ? items : sortItems(items)),
    [items, sortMode]
  )

  const avatarStackCount = maxDisplayedItems
    ? Math.min(maxDisplayedItems, items.length)
    : avatarStackCountForWidth({
        avatarSize,
        overlapAmount,
        width: measuredWidth,
      })

  const [visibleItems, moreItems] = useMemo(() => {
    if (orderedItems.length <= avatarStackCount) {
      return [orderedItems, null]
    }
    const visibleUserCount = avatarStackCount - (hideMoreItemsContainer ? 0 : 1)
    const visible = orderedItems.slice(0, visibleUserCount)
    const more = orderedItems.slice(visibleUserCount)
    return [visible, more]
  }, [orderedItems, avatarStackCount, hideMoreItemsContainer])

  const tooltipText = useMemo(() => {
    const text = moreItems?.map((user) => user.label)
    if (!text) return ''

    return new Intl.ListFormat(locale, { type: 'unit' }).format(text)
  }, [locale, moreItems])

  const showsSingleLabel = visibleItems.length === 1 && !hideMoreItemsContainer
  // With our current design, overflowing users are lumped into a '+M' item with a tooltip that lists all the names.
  // This isn't very good for accessibility: a better alternative would be letting the user open a dedicated list, but
  // there's not a design for it, plus we use this control in `<select>`-like components where the UX wouldn't work
  // anyways.
  //
  // The result of all this is `<AvatarList>` doesn't present as an actual list, so it's arguably better to completely
  // bypass normal accessibility logic, hiding the individual `<Avatar>`s and forcing a custom accessible label.
  const accessibleText = useMemo(() => {
    if (showsSingleLabel && !moreItems) return undefined

    const nameCount =
      orderedItems.length <= maxItemsInAccessibleText
        ? orderedItems.length
        : maxItemsInAccessibleText - 1
    const names = orderedItems.slice(0, nameCount).map(({ label }) => label)
    const restCount = orderedItems.length - nameCount
    if (restCount > 0) names.push(`${restCount.toLocaleString(locale)} more`)

    return new Intl.ListFormat(locale, { type: 'conjunction' }).format(names)
  }, [locale, moreItems, orderedItems, showsSingleLabel])

  if (items.length < 1) return null

  return (
    <div
      ref={setContainerEl}
      className={classNames(selectors.root, className)}
      data-unit={selectors.root}
      style={
        {
          [cssVariables.blockSize]: `${avatarSize}px`,
          [cssVariables.overlapAmount]: `${overlapAmount}px`,
        } as CSSProperties
      }
    >
      {visibleItems.map((item, i) => (
        <Avatar
          aria-hidden
          className={item.isActive === false ? selectors.inactive : undefined}
          fullName={item.label}
          initialsLength={initialsLength}
          key={item.uuid ?? i}
          locale={locale}
          size={avatarSize}
          src={item.avatarUrl}
          tooltipped={tooltipped}
        />
      ))}
      {showsSingleLabel && (
        <span
          aria-hidden={accessibleText !== undefined || undefined}
          part={`${parts.singleAvatarLabel} ${exportparts?.singleAvatarLabel ?? ''}`}
        >
          {visibleItems[0].label}
        </span>
      )}
      {moreItems && !hideMoreItemsContainer && (
        <Avatar
          aria-hidden
          fullName={moreItems.length.toLocaleString(locale, {
            compactDisplay: 'short',
            notation: 'compact',
            signDisplay: 'exceptZero',
          })}
          customTooltipText={tooltipText}
          initialsLength={10}
          mode="truncate"
          part={`${parts.overflow} ${exportparts?.overflow ?? ''}`}
          size={avatarSize}
          tooltipped={tooltipped}
        />
      )}
      {/* Last, so it doesn't change the avatars' `:nth-of-type` */}
      {accessibleText && <VisuallyHidden>{accessibleText}</VisuallyHidden>}
    </div>
  )
}
AvatarList.displayName = 'Lapidary: AvatarList'
